import { cn } from '@listening/utils/react-helpers';
import { Button } from '@listening/frontend/components/radix/button';
import { linkOptions, useRouter } from '@tanstack/react-router';
import { type ComponentProps, createContext, use } from 'react';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import { rootLogger } from '@/lib/root-logger';
import { taboolaTrackStartCheckout } from '@/server/lib/event-trackers';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { getDefaultCtaLabel } from './cta-label';

const removeTrailingSlash = (str: string) => str.replace(/\/$/, '');

const moduleLogger = rootLogger.child({ module: 'CtaButton' });

type CTAButtonContext = {
  label: string;
  href: string;
  analyticsCheckout: boolean;
  variant?: ComponentProps<typeof Button>['variant'];
};

/**
 * Where a CTA points when no page has overridden it.
 *
 * **The single definition of the default CTA destination.** Every CTA in the
 * app resolves through `useCtaHref()`, so changing this one `to` moves all of
 * them. `linkOptions` compile-checks it against the route tree, which is why
 * this is a link object rather than a string constant: a rename or deletion of
 * the destination route fails the typecheck here instead of shipping a 404 on
 * every marketing page at once.
 */
export const DEFAULT_CTA_LINK = linkOptions({ to: '/app/continue' });

const defaultCtaPropsBase: Omit<CTAButtonContext, 'label' | 'href'> = {
  analyticsCheckout: true,
};

// No `label` here on purpose: a default label would be spread over the
// `getDefaultCtaLabel(...)` result in `CtaButton` and shadow it, so the
// free-plan/trial gating would never render outside a provider. Providers
// that want a fixed label still set one.
//
// `href` is absent for the same class of reason: it lives in
// `DEFAULT_CTA_LINK`, resolved by `useCtaHref`. A default baked in here would
// also make "did an ancestor retarget this page's CTAs?" unanswerable, since
// every read would report a destination whether or not one was provided.
//
// **A nested provider must spread the value it inherits.** Setting only
// `label` replaces the whole context, which silently drops an ancestor's
// `href` and opts that subtree out of a page-level retarget. That is exactly
// how the nav bar sat outside `homepage-cta-funnel-v1` at first.
export const CtaButtonContext = createContext<Partial<CTAButtonContext>>({});

/**
 * The CTA destination for this position in the tree: whatever the nearest
 * `CtaButtonContext` provided, else `DEFAULT_CTA_LINK`.
 *
 * `<CtaButton>` uses it internally. Call it directly for the CTAs that are
 * plain `<Link>`/`<a>` rather than `<CtaButton>` — they lead to the same
 * place, so an experiment that retargets a page's CTAs has to move them too,
 * and hardcoding `/app/continue` at those call sites is what let the page fall
 * out of step with itself.
 *
 * Resolved through the router so the result carries the active locale prefix
 * (`/es/app/continue` on a Spanish page), the same rewrite `<Link>` applies.
 */
export function useCtaHref(): string {
  const router = useRouter();
  const providedHref = use(CtaButtonContext).href;
  return providedHref ?? router.buildLocation(DEFAULT_CTA_LINK).publicHref;
}

export function CtaButton({
  className,
  ...props
}: {
  className: string;
} & Partial<CTAButtonContext>) {
  const ctaContext = use(CtaButtonContext);
  const { isInFreePlanCountry } = useFreePlan();
  // Already resolves the context override, so the `...ctaContext` spread below
  // is a no-op for `href` — it stays in the list to keep the precedence chain
  // (defaults → context → explicit props) readable in one place.
  const ctaHref = useCtaHref();

  const defaultLabel = getDefaultCtaLabel(isInFreePlanCountry);

  const ctaProps: CTAButtonContext = {
    ...defaultCtaPropsBase,
    label: defaultLabel,
    href: ctaHref,
    ...ctaContext,
    ...props,
  };
  return <CTAButtonTryApp className={className} {...ctaProps} />;
}

function CTAButtonTryApp({
  className,
  label,
  href,
  analyticsCheckout,
  variant,
}: {
  className: string;
} & CTAButtonContext) {
  const { capture } = useAnalytics();
  return (
    <Button
      animated={variant === 'gradient'}
      asChild
      className={cn(
        'rounded-full px-8 py-7 text-xl hover:bg-black/80 focus:outline-none',
        className,
      )}
      variant={variant}
    >
      <a
        data-cta-button
        href={href}
        onClick={(e) => {
          moduleLogger.info('CTA button clicked');
          if (analyticsCheckout) {
            capture('cta_clicked', null);
            taboolaTrackStartCheckout();
          }

          const clickedAnchor = e.target as HTMLAnchorElement | undefined;
          const currentPathName = removeTrailingSlash(location.pathname);
          const clickedPathName = removeTrailingSlash(
            clickedAnchor?.pathname ?? '',
          );
          const clickLocationIsSamePage =
            currentPathName === clickedPathName &&
            location.hostname === clickedAnchor?.hostname;

          if (!(clickLocationIsSamePage && clickedAnchor.hash)) return;

          const hashTarget = document.querySelector(clickedAnchor.hash);
          if (hashTarget) {
            e.preventDefault();
            window.scrollTo({
              top: hashTarget.getBoundingClientRect().top + window.scrollY,
              behavior: 'smooth',
            });
          }
        }}
      >
        {label}
      </a>
    </Button>
  );
}
